import React, { useEffect, useMemo, useRef, useState } from "react";
import CommentBlock from "./CommentBlock";
import {
  PostDetailChevronDownIcon,
  PostDetailChevronUpIcon,
} from "../PostDetail/PostDetailCommentIcons";

const isNodeInView = (node) => {
  if (!node || typeof node.getBoundingClientRect !== "function") return true;
  const rect = node.getBoundingClientRect();
  return rect.top >= 8 && rect.bottom <= window.innerHeight - 8;
};

const replyTime = (reply) => {
  const time = new Date(reply && reply.createdAt).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const isAnalystReply = (reply) => reply?.user?.roleId === 3;

/** One conversation, in the order the replies were written. */
const chronologicalReplies = (replies) =>
  [...replies].sort((a, b) => replyTime(a) - replyTime(b));

/** More than two replies: show only the newest until the earlier ones are opened. */
const collapsedReplyPreview = (replies) => {
  if (replies.length <= 2) return replies;
  return replies.slice(-1);
};

const sortReplies = (replies) =>
  [...replies].sort((a, b) => {
    const aAnalyst = a.user?.roleId === 3 ? 0 : 1;
    const bAnalyst = b.user?.roleId === 3 ? 0 : 1;
    if (aAnalyst !== bAnalyst) return aAnalyst - bAnalyst;

    return replyTime(a) - replyTime(b);
  });

const renderReply = (reply, { user, onDelete, onLike, parentAuthorName, layout, justPosted }) => {
  const isAnalyst = isAnalystReply(reply);

  return (
    <article
      className={`comment-reply replyComments${
        isAnalyst ? " comment-reply--analyst" : ""
      }${justPosted ? " comment-reply--just-posted" : ""}`}
      id={reply.id}
      key={reply.id}
    >
      {justPosted && (
        <p className="comment-reply__posted" role="status">
          Reply posted
        </p>
      )}
      <CommentBlock
        comment={reply}
        user={user}
        canReply={false}
        onDelete={onDelete}
        onLike={onLike}
        replyTo={isAnalyst ? null : parentAuthorName}
        compact
        layout={layout}
      />
    </article>
  );
};

const CommentReplies = ({
  replies,
  parentAuthorName,
  user,
  onDelete,
  onLike,
  layout = "legacy",
  postedReplyId = null,
}) => {
  const sorted = useMemo(
    () =>
      layout === "postDetail" ? chronologicalReplies(replies) : sortReplies(replies),
    [replies, layout]
  );
  const analystCount = sorted.filter((r) => r.user?.roleId === 3).length;
  const [expanded, setExpanded] = useState(sorted.length <= 2);

  const analystReplies = sorted.filter((r) => r.user?.roleId === 3);
  const memberReplies = sorted.filter((r) => r.user?.roleId !== 3);
  const hiddenCount = expanded ? 0 : memberReplies.length;
  const visibleReplies = expanded ? sorted : analystReplies;

  const replyProps = { user, onDelete, onLike, parentAuthorName, layout };
  const sortedRef = useRef(sorted);
  sortedRef.current = sorted;

  useEffect(() => {
    if (layout !== "postDetail" || postedReplyId == null) return undefined;
    const replyKey = String(postedReplyId);
    const current = sortedRef.current;
    const mine = current.some((reply) => String(reply.id) === replyKey);
    if (!mine) return undefined;
    const preview = collapsedReplyPreview(current);
    const inPreview = preview.some((reply) => String(reply.id) === replyKey);
    if (!inPreview) setExpanded(true);
    const timer = window.setTimeout(() => {
      const node = document.getElementById(postedReplyId);
      if (!node || isNodeInView(node)) return;
      node.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, inPreview ? 60 : 140);
    return () => window.clearTimeout(timer);
  }, [layout, postedReplyId]);

  if (layout === "postDetail") {
    const preview = collapsedReplyPreview(sorted);
    const canToggle = preview.length < sorted.length;
    const visible = !canToggle || expanded ? sorted : preview;
    const earlierCount = sorted.length - preview.length;
    const earlierLabel = earlierCount === 1 ? "reply" : "replies";

    return (
      <div className="comment-replies comment-replies--post-detail">
        {canToggle && (
          <button
            type="button"
            className="comment-replies__more"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
          >
            {expanded ? (
              <PostDetailChevronUpIcon />
            ) : (
              <PostDetailChevronDownIcon />
            )}
            {expanded
              ? "Hide earlier replies"
              : `View ${earlierCount} earlier ${earlierLabel}`}
          </button>
        )}

        {visible.length > 0 && (
          <div className="comment-replies__list">
            {visible.map((reply) =>
              renderReply(reply, {
                ...replyProps,
                justPosted:
                  postedReplyId != null &&
                  String(reply.id) === String(postedReplyId),
              })
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="comment-replies">
      <div className="comment-replies__header">
        <div className="comment-replies__meta">
          <span className="material-icons comment-replies__icon" aria-hidden="true">
            forum
          </span>
          <p className="comment-replies__label">
            {replies.length} {replies.length === 1 ? "reply" : "replies"}
            {analystCount > 0 &&
              ` · ${analystCount} from analyst${analystCount === 1 ? "" : "s"}`}
          </p>
        </div>
        {replies.length > 2 && (
          <button
            type="button"
            className="comment-replies__toggle"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
          >
            {expanded ? "Hide" : "Show all"}
          </button>
        )}
      </div>

      {visibleReplies.length > 0 && (
        <div className="comment-replies__list">
          {visibleReplies.map((reply) => renderReply(reply, replyProps))}
        </div>
      )}

      {!expanded && hiddenCount > 0 && (
        <button
          type="button"
          className="comment-replies__more"
          onClick={() => setExpanded(true)}
        >
          Show {hiddenCount} more {hiddenCount === 1 ? "reply" : "replies"}
        </button>
      )}
    </div>
  );
};

export default CommentReplies;
